import { useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import RightArrowIcon from '@mui/icons-material/ChevronRightRounded';

import Input from 'shared/components/widgets/input';
import Button from 'shared/components/widgets/button';
import DatePicker from 'shared/components/widgets/date-picker';
import Autocomplete from 'shared/components/widgets/autocomplete';
import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';
import RouteLink from 'shared/components/widgets/route-link';

import { ROUTES } from 'constants/routes';
import { Controller, useForm } from 'react-hook-form';
import AutocompleteContainer from 'shared/components/widgets/autocomplete-container';
import InputContainer from 'shared/components/widgets/input-container';
import { customerDeliveryFileUpdateService } from '@services/private/customer-delivery/file-update.service';
import { is200xRange } from '@utils/utilities';
import { VALIDATION_MESSAGE } from 'constants/validation/message';
import { customerDeliveryFileDeleteService } from '@services/private/customer-delivery/file-delete.service';
import UploadFile from 'shared/components/widgets/upload-file';
import { customerDeliveryFileUploadStopService } from '@services/private/customer-delivery/file-upload/file-upload-stop';
import { customerDeliveryFileUploadInitiateService } from '@services/private/customer-delivery/file-upload/file-upload-initiate';
import { customerDeliveryFileUploadUrlAssignService } from '@services/private/customer-delivery/file-upload/file-upload-url-assign';
import { customerDeliveryFileUploadInsertService } from '@services/private/customer-delivery/file-upload/file-upload-insert';
import { customerDeliveryFileUploadCompleteService } from '@services/private/customer-delivery/file-upload/file-upload-complete';
import Dialog from 'shared/components/widgets/dialog';
import FileUploadingDialogContent from 'shared/components/dialog-content/file-uploading';
import { customerDeliveryFileDetailService } from '@services/private/customer-delivery/file-detail.service';
import { bytesToSize } from 'shared/helpers/bytes-to-size';
import MultipleFieldBox from 'shared/components/widgets/multiple-field-box';

type FileDetailData = {
  adminConfigEnv: { cssJsVersion: '' } | null;
  otmDetail: {
    authPwd: string;
    downloadUrl: string;
    expirationYmd: string;
    lfsUploadSt: '10' | '50' | '60' | '90';
    otmSeq: string;
    otmSt: 'allow' | 'deny';
    otmSubject: string;
    regDt: string;
    regName: string;
    regTp: string;
    updDt: string;
    updName: string;
    updTp: string;
    uploadFileList: [
      {
        otmfOriginName: string;
        otmfSize: string;
        regName: string;
        regTp: string;
      },
    ];
  } | null;
};

const CustomerDeliveryFileUpdate = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { retryUpload, fileData } = state;
  const [uploadProgress, setUploadProgress] = useState(0);
  const [otmfSeq, setOtmfSeq] = useState('');
  const [uploadId, setUploadId] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadedFile, setUploadedFile] = useState<any>(null);

  const [openUploadingDialog, setOpenUploadingDialog] = useState(false);
  const handleOpenUploading = () => setOpenUploadingDialog(true);
  const handleCloseUploading = () => setOpenUploadingDialog(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [fileDetailData, setFileDetailData] = useState<FileDetailData>({
    adminConfigEnv: null,
    otmDetail: null,
  });

  const abortControllersRef = useRef<AbortController[]>([]);


  const { SUBJECT, EXPIRATION_YMD } =
    VALIDATION_MESSAGE.CUSTOMER_DELIVERY.REGISTER_FILE;
  const customerDeliveryFileUpdateSchema = yup.object({
    subject: yup
      .string()
      .required(SUBJECT.REQUIRED)
      .min(3, SUBJECT.MIN_LENGTH)
      .max(100, SUBJECT.MAX_LENGTH)
      .default(state?.otmDetail?.otmSubject ?? ''),
    otmSt: yup.string(),
    expirationYmd: yup
      .string()
      .required(EXPIRATION_YMD.REQUIRED)
      .transform((_value, originalValue) => {
        return originalValue ? dayjs(originalValue).format('YYYY-MM-DD') : null;
      })
      .nullable()
      .matches(/^\d{4}-\d{2}-\d{2}$/, EXPIRATION_YMD.ALLOWED_DATE_FORMAT),
  });

  type CustomerDeliveryFileUpdateFormData = yup.InferType<
    typeof customerDeliveryFileUpdateSchema
  >;

  const {
    watch,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CustomerDeliveryFileUpdateFormData>({
    resolver: yupResolver(customerDeliveryFileUpdateSchema),
  });

  const fileStatusFilterData = [
    // { label: '전체', value: 'all' },
    { label: '다운로드 가능', value: 'allow' },
    { label: '다운로드 불가', value: 'deny' },
  ];

  const handleFileUploadService = async (otmSeq) => {
    const controller = new AbortController();
    abortControllersRef.current.push(controller);

    const {
      response: uploadInitiateResponse,
      statusCode: uploadInitiateStatusCode,
      error: uploadInitiateError,
    } = await customerDeliveryFileUploadInitiateService({
      otmSeq,
      fileName: uploadedFile?.name,
      fileSize: uploadedFile?.size,
    }, controller.signal);

    if (uploadInitiateError || !is200xRange(uploadInitiateStatusCode)) {
      handleCloseUploading();
    }

    if (
      uploadInitiateResponse?.code === '1' &&
      is200xRange(uploadInitiateStatusCode)
    ) {
      const { uploadId, partSize, otmfSeq } =
        uploadInitiateResponse?.result?.otmFileInfo;
      const totalParts = Math.ceil(uploadedFile?.size / partSize);
      const etags: {
        partNumber: number;
        etag: string;
      }[] = [];

      setUploadId(uploadId);
      setOtmfSeq(otmfSeq);

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const controller3 = new AbortController();
        abortControllersRef.current.push(controller3);

        setUploadProgress(Math.trunc((partNumber / totalParts) * 100));
        const {
          response: uploadUrlAssignResponse,
          statusCode: uploadUrlAssignStatusCode,
          error: uploadUrlAssignError,
        } = await customerDeliveryFileUploadUrlAssignService({
          otmSeq,
          otmfSeq,
          uploadId,
          partNumber,
        }, controller3.signal);
        if (uploadUrlAssignError || !is200xRange(uploadUrlAssignStatusCode)) {
          setUploadedFile(null);
          handleCloseUploading();
        }
        if (
          uploadUrlAssignResponse?.code === '1' &&
          is200xRange(uploadUrlAssignStatusCode)
        ) {
          const start = (partNumber - 1) * partSize;
          const end = Math.min(start + partSize, uploadedFile.size);
          const filePart = uploadedFile.slice(start, end);
          const { presignedUrl } = uploadUrlAssignResponse?.result?.otmFileInfo;

          // upload file
          const uploadResponse = await fetch(presignedUrl, {
            method: 'PUT',
            body: filePart,
            headers: {
              'Content-Type': uploadedFile.type,
            },
            signal: controller3.signal,
          });

          const etag = uploadResponse.headers.get('ETag')!;

          etags.push({ partNumber, etag });

          await customerDeliveryFileUploadInsertService({
            otmSeq,
            otmfSeq,
            partNumber,
            etag,
          }, controller3.signal);
        }
      }
      const {
        response: uploadCompleteResponse,
        statusCode: uploadCompleteStatusCode,
        error: uploadCompleteError,
      } = await customerDeliveryFileUploadCompleteService({
        otmSeq,
        otmfSeq,
        partArray: etags,
      }, controller.signal);

      if (uploadCompleteError || !is200xRange(uploadCompleteStatusCode)) {
        navigate(ROUTES.CUSTOMER_DELIVERY_FILES.ROOT);
        setUploadedFile(null);
      }

      if (
        uploadCompleteResponse?.code === '1' &&
        is200xRange(uploadCompleteStatusCode)
      ) {
        handleCloseUploading();
        navigate(ROUTES.CUSTOMER_DELIVERY_FILES.ROOT);
      }
    }
  };

  const stopUploading = async (
    otmSeq: string,
    otmfSeq: string,
    uploadId: string,
  ) => {
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current = [];
    const { response, statusCode } =
      await customerDeliveryFileUploadStopService({
        otmSeq,
        otmfSeq,
        uploadId,
      });

    if (response?.code === '1' && is200xRange(statusCode)) {
    }
  };

  const deleteCustomerDeliveryFile = async (otmSeq) => {
    const { response, statusCode } = await customerDeliveryFileDeleteService({
      otmSeq,
    });

    if (response?.code === '1' && is200xRange(statusCode)) {
      navigate(ROUTES.CUSTOMER_DELIVERY_FILES.ROOT);
    }
  };

  const handleFileUpdate = async (otmSeq, subject, otmSt, expirationYmd) => {
    const { response, statusCode } = await customerDeliveryFileUpdateService({
      otmSeq,
      subject,
      otmSt,
      expirationYmd,
    });

    if (response?.code === '1' && is200xRange(statusCode)) {
      navigate(ROUTES.CUSTOMER_DELIVERY_FILES.ROOT);
    }
  };

  const submitForm = (data: any) => {
    const { subject, otmSt, expirationYmd } = data;
    if (retryUpload) {
      handleFileUploadService(fileData?.otmSeq);
      handleOpenUploading();
    } else {
      handleFileUpdate(fileData?.otmSeq, subject, otmSt, expirationYmd);
    }
  };

  const handleFileData = (params) => {
    setFileDetailData((prevFileData) => {
      const newFileData = { ...prevFileData };
      params.forEach((item) => {
        newFileData[item.key] = item.value;
      });
      return newFileData;
    });
  };

  const getCustomerDeliveryFileDetail = async (fileId) => {
    const { response, statusCode } =
      await customerDeliveryFileDetailService(fileId);

    if (response?.code === '1' && is200xRange(statusCode)) {
      handleFileData([
        { key: 'adminConfigEnv', value: response?.result?.adminConfigEnv },
        { key: 'otmDetail', value: response?.result?.otmDetail },
      ]);
    }
  };
  useEffect(() => {
    getCustomerDeliveryFileDetail(fileData?.otmSeq);
    setValue('subject', fileData?.otmSubject ?? '');
    setValue(
      'expirationYmd',
      dayjs(fileData?.expirationYmd).format('YYYY-MM-DD'),
    );
    setValue('otmSt', fileData?.otmSt ?? 'allow');
  }, []);

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box marginBottom="18px">
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<RightArrowIcon sx={{ color: 'var(--neutral-800)' }} />}
        >
          <RouteLink to={ROUTES.CUSTOMER_DELIVERY_FILES.ROOT}>Home</RouteLink>
          <RouteLink to={ROUTES.CUSTOMER_DELIVERY_FILES.ROOT}>
            고객 전달용 대용량 파일
          </RouteLink>
          <RouteLink to={ROUTES.CUSTOMER_DELIVERY_FILES.ROOT}>
            파일 상세 정보
          </RouteLink>
          <RouteLink to={ROUTES.CUSTOMER_DELIVERY_FILES.ROOT} isActive>
            {retryUpload
              ? '대용량 파일 업로드 재시도'
              : '대용량 등록 파일 수정'}
          </RouteLink>
        </Breadcrumbs>
      </Box>
      <Box marginBottom="20px">
        <Typography variant="h6" color="var(--base-black)" fontWeight={700}>
          파일 상세 정보
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        component="form"
        onSubmit={handleSubmit(submitForm)}
        position="relative"
      >
        {retryUpload && (
          <Button
            type="button"
            variant="outlined"
            sx={{
              width: '112px',
              position: 'absolute',
              right: 0,
              top: '-24px',
            }}
            onClick={() => deleteCustomerDeliveryFile(fileData?.otmSeq)}
          >
            삭제하기
          </Button>
        )}
        <Box display="flex" flexDirection="column" gap={6} marginBottom="50px">
          {/* BASIC INFORMATION */}
          <FieldBox title="기본 정보">
            {/* UPLOAD TITLE */}
            <FieldBoxProperty
              name={retryUpload ? '업로드 제목' : '* 업로드 제목'}
              type={retryUpload ? 'text' : ''}
              value={
                retryUpload ? (
                  watch('subject')
                ) : (
                  <Controller
                    name="subject"
                    control={control}
                    render={({ field }) => {
                      return (
                        <InputContainer type="form">
                          <Input
                            {...field}
                            placeholder="검색어를 입력해 주세요."
                            defaultValue={field.value}
                            value={field.value}
                            showErrorMsg={!!errors.subject}
                            errorMessage={
                              errors.subject && errors.subject.message
                            }
                          />
                        </InputContainer>
                      );
                    }}
                  />
                )
              }
            />
            {/* EXPIRATION DATE */}
            <FieldBoxProperty
              name={retryUpload ? '만료일자' : '* 만료일자'}
              type={retryUpload ? 'text' : ''}
              value={
                retryUpload ? (
                  watch('expirationYmd')
                ) : (
                  <Box display="flex" flexDirection="row" gap={2}>
                    <Controller
                      name="expirationYmd"
                      control={control}
                      render={({ field }) => {
                        const { onChange, value, ...restField } = field;
                        return (
                          <DatePicker
                            {...restField}
                            defaultValue={dayjs(
                              state.otmDetail?.expirationYmd,
                              'YYYY-MM-DD',
                            )}
                            format="YYYY-MM-DD"
                            value={value ? dayjs(value, 'YYYY-MM-DD') : null}
                            open={showDatePicker}
                            closeAction={() => setShowDatePicker(false)}
                            selectToday={() => {
                              onChange(dayjs());
                              setShowDatePicker(false);
                            }}
                            onChange={(date: Dayjs | null) => {
                              onChange(date ? date.format('YYYY-MM-DD') : null);
                            }}
                            showErrorMsg={!!errors.expirationYmd}
                            errorMessage={
                              errors.expirationYmd &&
                              errors.expirationYmd.message
                            }
                          />
                        );
                      }}
                    />

                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => setShowDatePicker(true)}
                    >
                      달력
                    </Button>
                  </Box>
                )
              }
            />
            {/* FILE STATUS */}
            <FieldBoxProperty
              name="파일상태"
              type={retryUpload ? 'text' : ''}
              value={
                retryUpload ? (
                  '다운로드 가능'
                ) : (
                  <AutocompleteContainer type="form">
                    <Controller
                      name="otmSt"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Autocomplete
                            options={fileStatusFilterData}
                            defaultValue={
                              fileStatusFilterData.find(
                                (el) => el.value === field?.value,
                              ) ?? {
                                label: '다운로드 가능',
                                value: 'allow',
                              }
                            }
                            value={field?.value ?? 'allow'}
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            onChange={(_, newValue, reason) => {
                              field.onChange(newValue?.value);
                              if (reason === 'clear') {
                                field.onChange('allow');
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </AutocompleteContainer>
                )
              }
            />
            {/* PASSWORD */}
            <FieldBoxProperty
              name="패스워드"
              value={
                <Typography variant="body1" color="var(--gray-800)">
                  {fileDetailData?.otmDetail?.authPwd ?? ''}
                </Typography>
              }
            />
          </FieldBox>

          {/* UPLOAD FILE */}
          {retryUpload && (
            <FieldBox title="업로드 파일">
              <FieldBoxProperty
                name="* 업로드 파일"
                value={
                  <Box>
                    <UploadFile
                      uploadedFile={uploadedFile}
                      setUploadedFile={setUploadedFile}
                    />
                    {!uploadedFile && uploadError && (
                      <Typography color={'var(--danger-500)'}>
                        {uploadError}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </FieldBox>
          )}

          {/* UPLOAD FILE INFORMATION*/}
          {!retryUpload && (
            <MultipleFieldBox title="업로드 파일 정보">
              <TableBody>
                {state?.otmDetail?.uploadFileList.map((uploadItem, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell>{uploadItem?.otmfOriginName}</TableCell>
                      <TableCell>
                        {bytesToSize(Number(uploadItem?.otmfSize) || 0)}
                      </TableCell>
                      <TableCell>
                        {uploadItem?.regTp === 'admin'
                          ? '관리자'
                          : uploadItem?.regTp === 'staff'
                            ? '직원'
                            : '--'}
                      </TableCell>
                      <TableCell>{uploadItem?.regName}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </MultipleFieldBox>
          )}
          <Box display="flex" gap="14px">
            {/* CANCEL */}
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(ROUTES.CUSTOMER_DELIVERY_FILES.ROOT)}
            >
              취소
            </Button>
            {/* SAVE */}
            {retryUpload ? (
              <Button
                variant="contained"
                color="primary"
                type={uploadedFile ? 'submit' : 'button'}
                onClick={() => {
                  if (!uploadedFile) {
                    setUploadError('파일을 업로드 해주세요.');
                  } else {
                    setUploadError('');
                  }
                }}
              >
                파일 다시 업로드 하기
              </Button>
            ) : (
              <Button variant="contained" color="primary" type="submit">
                저장하기
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <Dialog
        isBackDropClickClose={false}
        open={openUploadingDialog}
        closeAction={handleCloseUploading}
        width={348}
        height={304}
        isCloseIcon={false}
      >
        <FileUploadingDialogContent
          closeAction={handleCloseUploading}
          progress={uploadProgress}
          stopAction={() => stopUploading(fileData?.otmSeq, otmfSeq, uploadId)}
        />
      </Dialog>
    </Box>
  );
};

export default CustomerDeliveryFileUpdate;
